import React, { useState, useMemo } from "react";
import { Icon } from "../../UIElements/Icon";
import { generateWidgetCode } from "./widgetCodeGenerators";

const WidgetCodeSnippet = ({ config, widgetType, widgetName }) => {
  const [copied, setCopied] = useState(false);

  const code = useMemo(
    () => generateWidgetCode(widgetType, config),
    [widgetType, config],
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([code], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `finitless-widget-${widgetType}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const manualSetupWidgets = ["popup"];
  const requiresManualSetup = manualSetupWidgets.includes(widgetType);

  return (
    <div className="space-y-4">
      {/* Manual Setup Warning */}
      {requiresManualSetup && (
        <div className="flex gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4">
          <Icon
            name="AlertTriangle"
            className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500"
          />
          <div>
            <h4 className="mb-1 font-medium text-amber-500">
              Additional Setup Required
            </h4>
            <p className="text-sm text-amber-200/80">
              This widget requires you to create your own button to trigger the
              modal. Copy only the{" "}
              <code className="rounded bg-amber-500/20 px-1.5 py-0.5 text-xs">
                &lt;script&gt;
              </code>{" "}
              tag and call{" "}
              <code className="rounded bg-amber-500/20 px-1.5 py-0.5 text-xs">
                window.openFinitlessModal()
              </code>{" "}
              from your button.
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-card-foreground font-semibold">
            Widget Code: {widgetName}
          </h3>
          <p className="text-muted-foreground mt-1 text-sm">
            Copy this code and paste it into your website before the
            &lt;/body&gt; tag
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleDownload}
            className="bg-accent hover:bg-accent/80 text-card-foreground flex items-center gap-2 rounded-lg px-4 py-2 transition-colors"
          >
            <Icon name="Download" className="h-4 w-4" />
            <span className="hidden sm:inline">Download</span>
          </button>
          <button
            onClick={handleCopy}
            className={`flex items-center gap-2 rounded-lg px-4 py-2 transition-all ${
              copied
                ? "bg-green-500 text-white"
                : "bg-brand-gradient text-white hover:opacity-90"
            } `}
          >
            <Icon name={copied ? "Check" : "Copy"} className="h-4 w-4" />
            <span className="hidden sm:inline">
              {copied ? "Copied!" : "Copy"}
            </span>
          </button>
        </div>
      </div>

      {/* Code Block */}
      <div className="border-border relative overflow-hidden rounded-xl border">
        <div className="border-border flex items-center justify-between border-b bg-[#1e1e1e] px-4 py-2">
          <div className="flex items-center gap-2">
            <div className="flex gap-1.5">
              <div className="h-3 w-3 rounded-full bg-red-500" />
              <div className="h-3 w-3 rounded-full bg-yellow-500" />
              <div className="h-3 w-3 rounded-full bg-green-500" />
            </div>
            <span className="ml-2 text-xs text-gray-400">
              widget-{widgetType}.html
            </span>
          </div>
          <span className="text-xs text-gray-500">HTML + JavaScript</span>
        </div>

        <pre className="max-h-[500px] overflow-x-auto overflow-y-auto bg-[#1e1e1e] p-4">
          <code className="font-mono text-sm whitespace-pre text-[#e0e0e0]">
            {code}
          </code>
        </pre>
      </div>

      {/* Instructions */}
      <div className="bg-accent/50 border-border rounded-xl border p-4">
        <h4 className="text-card-foreground mb-3 flex items-center gap-2 font-medium">
          <Icon name="Info" className="text-brand-cyan h-4 w-4" />
          Integration Instructions
        </h4>

        {widgetType === "floating" && (
          <ol className="text-muted-foreground list-inside list-decimal space-y-2 text-sm">
            <li>Copy all the code above</li>
            <li>
              Paste it right before the{" "}
              <code className="bg-background rounded px-1.5 py-0.5 text-xs">
                &lt;/body&gt;
              </code>{" "}
              tag in your HTML
            </li>
            <li>
              The floating button will automatically appear in the corner of
              your site
            </li>
            <li>
              You can call{" "}
              <code className="bg-background rounded px-1.5 py-0.5 text-xs">
                toggleFinitlessChat()
              </code>{" "}
              to open/close programmatically
            </li>
          </ol>
        )}

        {widgetType === "popup" && (
          <ol className="text-muted-foreground list-inside list-decimal space-y-2 text-sm">
            <li>
              Copy the script and paste it before{" "}
              <code className="bg-background rounded px-1.5 py-0.5 text-xs">
                &lt;/body&gt;
              </code>{" "}
              or in your{" "}
              <code className="bg-background rounded px-1.5 py-0.5 text-xs">
                index.html
              </code>
            </li>
            <li>
              Call{" "}
              <code className="bg-background rounded px-1.5 py-0.5 text-xs">
                window.openFinitlessModal()
              </code>{" "}
              from any button or link
            </li>
            <li>
              In React:{" "}
              <code className="bg-background rounded px-1.5 py-0.5 text-xs">
                onClick=&#123;() =&gt; window.openFinitlessModal()&#125;
              </code>
            </li>
            <li>
              The modal closes with the close button, clicking outside, or
              calling{" "}
              <code className="bg-background rounded px-1.5 py-0.5 text-xs">
                window.closeFinitlessModal()
              </code>
            </li>
          </ol>
        )}

        {widgetType === "inline" && (
          <ol className="text-muted-foreground list-inside list-decimal space-y-2 text-sm">
            <li>
              Add{" "}
              <code className="bg-background rounded px-1.5 py-0.5 text-xs">
                &lt;div id=&quot;finitless-chat-inline&quot;&gt;&lt;/div&gt;
              </code>{" "}
              where you want the chat to appear
            </li>
            <li>
              Copy the{" "}
              <code className="bg-background rounded px-1.5 py-0.5 text-xs">
                &lt;script&gt;
              </code>{" "}
              and paste it in your{" "}
              <code className="bg-background rounded px-1.5 py-0.5 text-xs">
                index.html
              </code>{" "}
              before{" "}
              <code className="bg-background rounded px-1.5 py-0.5 text-xs">
                &lt;/body&gt;
              </code>
            </li>
            <li>
              In React: add the div in your component JSX, and the script in{" "}
              <code className="bg-background rounded px-1.5 py-0.5 text-xs">
                public/index.html
              </code>
            </li>
            <li>The chat will display directly in the flow of your page</li>
          </ol>
        )}
      </div>
    </div>
  );
};

export default WidgetCodeSnippet;
